import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Search, Pencil, Trash2, Star, Phone, Mail, MapPin, Key, ShieldOff, ShieldCheck, Copy, Eye, EyeOff, FileText, ShieldX, Send } from 'lucide-react';
import { toast } from 'sonner';
import SubcontractorMap from '@/components/SubcontractorMap';
import PendingSubcontractorsPanel from '@/components/PendingSubcontractorsPanel';
import NotifySubcontractorsDialog from '@/components/NotifySubcontractorsDialog';
import useFocusItem from '@/hooks/useFocusItem';

const INITIAL_FORM = {
  company_name: '', contact_name: '', email: '', phone: '', specialty: '',
  address: '', country: 'France', notes: '', active: true,
  quality_rating: 3, reliability_rating: 3, delay_rating: 3,
};

const COUNTRY_OPTIONS = [
  'France', 'Belgique', 'Suisse', 'Luxembourg', 'Allemagne', 'Italie', 'Espagne',
  'Portugal', 'Pays-Bas', 'Royaume-Uni', 'Pologne', 'République tchèque', 'Roumanie',
  'Maroc', 'Tunisie', 'Algérie', 'Turquie', 'Chine', 'Vietnam', 'Inde', 'États-Unis', 'Canada',
];

const RatingStars = ({ value, onChange, readOnly = false }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <button key={i} type="button" disabled={readOnly}
        onClick={() => onChange && onChange(i)}
        className={`${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
      >
        <Star className={`w-4 h-4 ${i <= value ? 'text-amber-500 fill-amber-500' : 'text-zinc-700'}`} />
      </button>
    ))}
  </div>
);

export default function SubcontractorsPage() {
  const [subs, setSubs] = useState([]);
  useFocusItem(subs);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [search, setSearch] = useState('');
  const [credentialsDialog, setCredentialsDialog] = useState(null);
  const [passwordDialog, setPasswordDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);

  const fetchSubs = useCallback(async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const { data } = await api.get(`/subcontractors${params}`);
      setSubs(data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  const handleSave = async () => {
    try {
      if (editingSub) {
        await api.put(`/subcontractors/${editingSub.id}`, form);
        toast.success('Sous-traitant mis à jour');
        setDialogOpen(false);
      } else {
        const { data } = await api.post('/subcontractors', form);
        toast.success('Sous-traitant créé');
        setDialogOpen(false);
        // Show credentials
        setCredentialsDialog({
          company: data.company_name,
          login: data.login_email,
          password: data.generated_password,
        });
      }
      setEditingSub(null);
      setForm(INITIAL_FORM);
      fetchSubs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleEdit = (sub) => {
    setEditingSub(sub);
    setForm({
      company_name: sub.company_name || '',
      contact_name: sub.contact_name || '',
      email: sub.email || '',
      phone: sub.phone || '',
      specialty: sub.specialty || '',
      address: sub.address || '',
      country: sub.country || 'France',
      notes: sub.notes || '',
      active: sub.active !== false,
      quality_rating: sub.quality_rating || 3,
      reliability_rating: sub.reliability_rating || 3,
      delay_rating: sub.delay_rating || 3,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce sous-traitant et son compte utilisateur ?')) return;
    try { await api.delete(`/subcontractors/${id}`); toast.success('Supprimé'); fetchSubs(); }
    catch { toast.error('Erreur'); }
  };

  const handleToggleAccess = async (sub) => {
    try {
      const { data } = await api.put(`/subcontractors/${sub.id}/toggle-access`);
      toast.success(data.message);
      fetchSubs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 4) { toast.error('Mot de passe trop court'); return; }
    try {
      await api.put(`/subcontractors/${passwordDialog.id}/password`, { new_password: newPassword });
      toast.success('Mot de passe modifié');
      setPasswordDialog(null);
      setNewPassword('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié');
  };

  const handleNew = () => { setEditingSub(null); setForm(INITIAL_FORM); setDialogOpen(true); };

  const downloadNda = async (sub) => {
    try {
      const res = await api.get(`/nda/${sub.user_id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const safe = (sub.company_name || 'sous-traitant').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40);
      a.download = `NDA_${safe}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur de téléchargement');
    }
  };

  return (
    <div className="space-y-6" data-testid="subcontractors-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Sous-traitants</h1>
          <p className="text-sm text-zinc-500 mt-1">{subs.length} sous-traitant(s)</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="notify-subs-btn"
            onClick={() => setNotifyOpen(true)}
            variant="outline"
            className="border-zinc-700 text-zinc-200 hover:bg-zinc-800 rounded-sm"
          >
            <Send className="w-4 h-4 mr-2" /> Notifier
          </Button>
          <Button data-testid="new-sub-btn" onClick={handleNew} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
            <Plus className="w-4 h-4 mr-2" /> Nouveau sous-traitant
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input data-testid="sub-search" placeholder="Rechercher un sous-traitant..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm" />
        </div>
      </div>

      <PendingSubcontractorsPanel onChanged={fetchSubs} />

      <SubcontractorMap />

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm h-56 nexus-shimmer" />)}
        </div>
      ) : subs.length === 0 ? (
        <div className="text-center py-16 text-zinc-500">Aucun sous-traitant enregistré</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {subs.map((sub) => (
            <Card key={sub.id} data-focus-id={sub.id} className="bg-zinc-900 border-zinc-800 rounded-sm nexus-card" data-testid={`sub-card-${sub.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-base font-semibold text-zinc-100">{sub.company_name}</h3>
                    {sub.contact_name && <p className="text-sm text-zinc-400">{sub.contact_name}</p>}
                    {sub.country && (
                      <p className="text-[11px] text-zinc-500 mt-0.5"><MapPin className="w-3 h-3 inline mr-0.5" />{sub.country}</p>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-sm border ${sub.account_active ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border-rose-500/20'}`}>
                    {sub.account_active ? 'Actif' : 'Accès révoqué'}
                  </span>
                </div>

                {/* NDA status */}
                <div className="mb-3" data-testid={`nda-status-${sub.id}`}>
                  {sub.nda_signed ? (
                    <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-sm bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" data-testid={`nda-signed-${sub.id}`}>
                      <ShieldCheck className="w-3 h-3" />
                      NDA signé
                      {sub.nda_signed_at && <span className="text-emerald-400/70">· {new Date(sub.nda_signed_at).toLocaleDateString('fr-FR')}</span>}
                      {sub.nda_contract_version && <span className="text-emerald-400/50">v{sub.nda_contract_version}</span>}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-sm bg-rose-500/10 text-rose-300 border border-rose-500/30" data-testid={`nda-unsigned-${sub.id}`}>
                      <ShieldX className="w-3 h-3" />
                      NDA non signé
                    </span>
                  )}
                </div>

                {sub.specialty && (
                  <span className="inline-flex text-xs bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-sm mb-3">{sub.specialty}</span>
                )}

                {/* Credentials */}
                {sub.login_email && (
                  <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-2.5 mb-3">
                    <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Identifiants de connexion</p>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-blue-400 font-mono">{sub.login_email}</span>
                      <button onClick={() => copyToClipboard(sub.login_email)} className="text-zinc-600 hover:text-zinc-300 transition-colors">
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                    {sub.plain_password && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-amber-400/80 font-mono">{sub.plain_password}</span>
                        <button onClick={() => copyToClipboard(sub.plain_password)} className="text-zinc-600 hover:text-zinc-300 transition-colors">
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-1.5 text-xs text-zinc-500 mb-3">
                  {sub.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3" />{sub.email}</div>}
                  {sub.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3" />{sub.phone}</div>}
                  {sub.address && <div className="flex items-center gap-2"><MapPin className="w-3 h-3" />{sub.address}</div>}
                </div>

                <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                  <div><p className="text-zinc-600 mb-0.5">Qualité</p><RatingStars value={sub.quality_rating} readOnly /></div>
                  <div><p className="text-zinc-600 mb-0.5">Fiabilité</p><RatingStars value={sub.reliability_rating} readOnly /></div>
                  <div><p className="text-zinc-600 mb-0.5">Délais</p><RatingStars value={sub.delay_rating} readOnly /></div>
                </div>

                {/* Quality score (auto: +5% perfect / -10% issue) */}
                <div className="mb-3" data-testid={`quality-score-${sub.id}`}>
                  <div className="flex items-center justify-between text-[10px] text-zinc-600 uppercase tracking-wider mb-1">
                    <span>Score qualité auto</span>
                    <span className={`font-mono ${(sub.quality_score ?? 100) >= 70 ? 'text-emerald-400' : (sub.quality_score ?? 100) >= 40 ? 'text-amber-400' : 'text-rose-400'}`}>{sub.quality_score ?? 100}%</span>
                  </div>
                  <div className="h-1.5 bg-zinc-800 rounded-sm overflow-hidden">
                    <div className={`h-full transition-all ${(sub.quality_score ?? 100) >= 70 ? 'bg-emerald-500' : (sub.quality_score ?? 100) >= 40 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${sub.quality_score ?? 100}%` }} />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-zinc-800 mb-2">
                  <div className="text-xs text-zinc-500">
                    <span className="text-zinc-300 font-medium">{sub.total_projects || 0}</span> projets
                    <span className="mx-1.5 text-zinc-700">|</span>
                    <span className="text-zinc-300 font-medium">{sub.active_projects || 0}</span> actifs
                    <span className="mx-1.5 text-zinc-700">|</span>
                    {formatCurrency(sub.total_volume || 0)}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 pt-2 border-t border-zinc-800/50">
                  <button data-testid={`edit-sub-${sub.id}`} onClick={() => handleEdit(sub)} className="p-1.5 text-zinc-500 hover:text-zinc-100 transition-colors" title="Modifier">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button data-testid={`password-sub-${sub.id}`} onClick={() => { setPasswordDialog(sub); setNewPassword(''); }} className="p-1.5 text-zinc-500 hover:text-blue-400 transition-colors" title="Changer mot de passe">
                    <Key className="w-3.5 h-3.5" />
                  </button>
                  <button data-testid={`toggle-sub-${sub.id}`} onClick={() => handleToggleAccess(sub)}
                    className={`p-1.5 transition-colors ${sub.account_active ? 'text-zinc-500 hover:text-rose-400' : 'text-zinc-500 hover:text-emerald-400'}`}
                    title={sub.account_active ? "Révoquer l'accès" : "Réactiver l'accès"}>
                    {sub.account_active ? <ShieldOff className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                  </button>
                  {sub.nda_signed && sub.user_id && (
                    <button
                      onClick={() => downloadNda(sub)}
                      className="p-1.5 text-zinc-500 hover:text-emerald-400 transition-colors"
                      title="Télécharger le contrat NDA signé"
                      data-testid={`download-nda-${sub.id}`}
                    >
                      <FileText className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button data-testid={`delete-sub-${sub.id}`} onClick={() => handleDelete(sub.id)} className="p-1.5 text-zinc-500 hover:text-rose-400 transition-colors ml-auto" title="Supprimer">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-xl max-h-[85vh] overflow-y-auto" data-testid="sub-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {editingSub ? 'Modifier' : 'Nouveau sous-traitant'}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {editingSub ? 'Modifiez les informations.' : 'Un identifiant et mot de passe seront générés automatiquement.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Société *</Label>
              <Input data-testid="form-sub-company" value={form.company_name} onChange={(e) => setForm(f => ({ ...f, company_name: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" placeholder="Nom de la société" />
              {!editingSub && form.company_name && (
                <p className="text-[11px] text-blue-400 font-mono">Identifiant prévu : {form.company_name.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 20)}@prodpilot.fr</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Contact *</Label>
              <Input value={form.contact_name} onChange={(e) => setForm(f => ({ ...f, contact_name: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" placeholder="Prénom Nom" />
              {!editingSub && form.contact_name && (
                <p className="text-[11px] text-zinc-500">Mot de passe prévu : {(form.contact_name.split(' ')[0] || 'user').replace(/[^a-zA-Z]/g, '').slice(0, 1).toUpperCase() + (form.contact_name.split(' ')[0] || 'user').replace(/[^a-zA-Z]/g, '').slice(1)}2024!</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Email professionnel</Label>
              <Input value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Téléphone</Label>
              <Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Spécialité</Label>
              <Input value={form.specialty} onChange={(e) => setForm(f => ({ ...f, specialty: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Adresse</Label>
              <Input value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Pays</Label>
              <select
                value={form.country}
                onChange={(e) => setForm(f => ({ ...f, country: e.target.value }))}
                className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 rounded-sm h-9 px-3 text-sm"
                data-testid="form-sub-country"
              >
                {COUNTRY_OPTIONS.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Qualité</Label>
              <RatingStars value={form.quality_rating} onChange={(v) => setForm(f => ({ ...f, quality_rating: v }))} />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Fiabilité</Label>
              <RatingStars value={form.reliability_rating} onChange={(v) => setForm(f => ({ ...f, reliability_rating: v }))} />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Respect des délais</Label>
              <RatingStars value={form.delay_rating} onChange={(v) => setForm(f => ({ ...f, delay_rating: v }))} />
            </div>
            <div className="col-span-2 space-y-2">
              <Label className="text-zinc-400 text-sm">Notes internes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Annuler</Button>
            <Button data-testid="save-sub-btn" onClick={handleSave} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
              {editingSub ? 'Mettre à jour' : 'Créer le sous-traitant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog open={!!credentialsDialog} onOpenChange={() => setCredentialsDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md" data-testid="credentials-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-emerald-400" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Sous-traitant créé avec succès
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              Voici les identifiants de connexion. Communiquez-les au sous-traitant.
            </DialogDescription>
          </DialogHeader>
          {credentialsDialog && (
            <div className="space-y-4 py-4">
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4 space-y-3">
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Société</p>
                  <p className="text-sm text-zinc-200 font-medium">{credentialsDialog.company}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Identifiant de connexion</p>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-blue-400 font-mono">{credentialsDialog.login}</p>
                    <button onClick={() => copyToClipboard(credentialsDialog.login)} className="text-zinc-500 hover:text-zinc-200">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Mot de passe</p>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-amber-400 font-mono">{credentialsDialog.password}</p>
                    <button onClick={() => copyToClipboard(credentialsDialog.password)} className="text-zinc-500 hover:text-zinc-200">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              <p className="text-xs text-rose-400/80">Attention : le mot de passe ne sera plus affiché après fermeture. Notez-le maintenant.</p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCredentialsDialog(null)} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium w-full">
              Compris, fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={!!passwordDialog} onOpenChange={() => setPasswordDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-sm" data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Changer le mot de passe
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {passwordDialog?.company_name} - {passwordDialog?.login_email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  data-testid="new-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Nouveau mot de passe"
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm pr-10"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialog(null)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Annuler</Button>
            <Button data-testid="save-password-btn" onClick={handleChangePassword} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
              Modifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {notifyOpen && (
        <NotifySubcontractorsDialog
          open={notifyOpen}
          onOpenChange={setNotifyOpen}
          subs={subs}
        />
      )}
    </div>
  );
}
